<template>
  <ClientOnly>
    <!-- CLIENT SLOT: only renders on the client; avoids hydration on this subtree -->
    <PortableText
      v-if="portableTextJson"
      :value="portableTextJson"
      :components="components"
      :onMissingComponent="onMissingComponent"
      v-once
    />

    <!-- SERVER FALLBACK: rendered during SSR so crawlers/users see content immediately -->
    <template #fallback>
      <PortableText
        v-if="portableTextJsonNoBrs"
        :value="portableTextJsonNoBrs"
        :components="components"
        :onMissingComponent="onMissingComponent"
      />
    </template>
  </ClientOnly>
</template>

<script setup lang="ts">
import { isEmpty } from '~/utils/kontent-helper'
import {
  transformToPortableText,
  nodeParse,
  browserParse,
  type PortableTextObject
} from '@kontent-ai/rich-text-resolver'
import { PortableText } from '@portabletext/vue'
import type {
  PortableTextMarkComponent,
  PortableTextTypeComponent,
  PortableTextVueComponents
} from '@portabletext/vue'
import { PtComponent, PtImage, PtItemLink, PtSuperscript, PtTable } from '#components'

const config = useRuntimeConfig()
const props = defineProps<{ value: string | undefined }>()
const portableTextJson = ref<any>(null)
let portableTextJsonNoBrs: PortableTextObject[] = []

if (props.value && !isEmpty(props.value)) {
  try {
    const richTextValue = props.value
    if (import.meta.client) {
      portableTextJson.value = transformToPortableText(browserParse(richTextValue))
      portableTextJsonNoBrs = transformToPortableText(browserParse(removeBrs(richTextValue)))
    } else {
      portableTextJson.value = transformToPortableText(nodeParse(richTextValue))
      portableTextJsonNoBrs = transformToPortableText(nodeParse(removeBrs(richTextValue)))
    }
  } catch (e) {
    console.error('Error parsing value', props.value)
    console.error('Error parsing JSON', e)
  }
}

const components = {
  types: {
    component: PtComponent,
    image: PtImage,
    table: PtTable
  } as PortableTextTypeComponent,
  marks: {
    internalLink: PtItemLink,
    link: PtItemLink,
    sup: PtSuperscript
  } as PortableTextMarkComponent
} as PortableTextVueComponents

const onMissingComponent = (message: string, options: any) => {
  if (config.public.isProduction) return
  console.log(message, {
    type: options.type, // e.g. `someUnknownType`
    nodeType: options.nodeType // 'block' | 'mark' | 'blockStyle' | 'listStyle' | 'listItemStyle'
  })
}

function removeBrs(html: string) {
  const noBrHtml = html.replace(/<br\s*\/?>/gi, ' ')
  return noBrHtml
}
</script>
